import { ContractDetailContractVersionFragment } from 'generated/resources/graphql'
import { minBy, orderBy } from 'lodash'
import moment, { Moment } from 'moment'

import { stringToDate } from 'modules/core/lib/dates'

type OrderType = 'desc' | 'asc'

class EmployeeContract {
  startsOn: Moment
  endsOn: Moment | null
  versions: ContractDetailContractVersionFragment[]

  constructor(startsOn: string | Moment) {
    this.startsOn = moment(startsOn)
    this.endsOn = null
    this.versions = []
  }

  sortedVersions(order: OrderType = 'desc') {
    return orderBy(this.versions, ['effectiveOn'], [order])
  }

  /**
   * We need to calculate the min effective on until we have some date validation.
   * If we don't do this, we can have inconsistencies with contracts that
   * has a start on date lower than the first effective on date.
   **/
  effectiveOn(): Moment | null {
    const minEffectiveOnVersion = minBy(this.versions, 'effectiveOn')
    if (!minEffectiveOnVersion?.effectiveOn) return null
    const effectiveOn = stringToDate(minEffectiveOnVersion.effectiveOn, 'backend')
    if (!effectiveOn.isValid()) return null

    return effectiveOn
  }

  pushVersion(version: ContractDetailContractVersionFragment) {
    this.versions.push(version)
  }

  includesVersion(versionId: string): boolean {
    return !!this.versions.find(({ id }) => id === versionId)
  }

  formattedStartOn(format = 'MMM Do YYYY'): string {
    return this.startsOn.format(format)
  }

  hasEndDate(): boolean {
    return this.endsOn !== null
  }

  isOngoing(): boolean {
    const now = moment()
    const targetDate = this.effectiveOn() || this.startsOn
    return !targetDate.isAfter(now) && !this.hasExpired()
  }

  hasExpired(): boolean {
    const now = moment()
    return this.endsOn !== null && this.endsOn.isBefore(now)
  }

  isUpcoming(): boolean {
    const now = moment()
    const targetDate = this.effectiveOn() ?? this.startsOn
    return targetDate.isAfter(now)
  }
}

export default EmployeeContract
